import React, { useEffect, useRef, useState } from "react";
import { Grid, Typography } from "@mui/material";
import "../../assets/css/UploadDocument.css";
import { service } from "../../model/service";

const ALLOWED = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".docx",
  ".msg",
  ".eml",
  ".txt",
  ".odt",
  ".dot",
  ".xml",
];
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

const DocumentUpload = ({
  label,
  padding,
  noGrid,
  isIdentityDocument,
  documentSide,
  documents,
  existingDocument,
  refreshDocuments,
  saveAmlData,
  hasError,
  languange,
  maxSize = DEFAULT_MAX_SIZE,
}) => {
  const inputRef = useRef(null);
  const t = (ita, eng) => (languange === "ENG" ? eng : ita);

  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [file, setFile] = useState(
    existingDocument
      ? { name: existingDocument.original_name, id: existingDocument.id }
      : null,
  );

  useEffect(() => {
    if (existingDocument?.id) {
      setFile({
        name: existingDocument.original_name,
        id: existingDocument.id,
      });
    } else {
      setFile(null);
    }
  }, [existingDocument?.id]);

  const validate = (f) => {
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      setError(t("Estensione non supportata", "Unsupported file extension"));
      return false;
    }
    if (f.size > maxSize) {
      setError(t("File supera la dimensione massima consentita di 4,5 MB", "File exceeds the maximum allowed size of 4.5 MB"));
      return false;
    }
    setError(null);
    return true;
  };

  const handleFile = async (f) => {
    if (!validate(f)) return;
    setFile(f);

    if (isIdentityDocument) {
      try {
        setUploading(true);
        const formData = new FormData();
        const ext = f.name.split(".").pop().toLowerCase();
        const assignedName = documentSide ? `${documentSide}.${ext}` : f.name;
        formData.append("files[]", f);
        formData.append("assigned_name", assignedName);
        await service.uploadIdentityDocument(formData);

        if (typeof refreshDocuments === "function") await refreshDocuments();
        if (typeof saveAmlData === "function") await saveAmlData();
      } catch (err) {
        setError(
          t(
            "Errore nel caricamento del documento",
            "Error uploading the document",
          ),
        );
        setFile(null);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const content = (
    <>
      <Typography
        className="color-blue-dark title-input"
        style={{ marginBottom: "4px" }}
      >
        {label}
      </Typography>

      <div
        onClick={() => !file && !uploading && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "0 14px",
          height: "56px",
          borderRadius: "10px",
          border: `1px solid ${error || hasError ? "#bc2957" : dragOver ? "#1976d2" : "rgba(0,0,0,0.23)"}`,
          backgroundColor: "#ececec",
          cursor: file || uploading ? "default" : "pointer",
          boxSizing: "border-box",
          marginTop: "0px",
          opacity: uploading ? 0.7 : 1,
        }}
      >
        <div
          className="icon-upload-document"
          style={{ flexShrink: 0, width: "22px", height: "22px" }}
        />

        <Typography
          className="color-blue-dark"
          style={{
            flex: 1,
            fontSize: "14px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            marginTop: "3px",
          }}
        >
          {uploading
            ? t("Caricamento in corso...", "Uploading...")
            : file
              ? file.original_name_attached || file.name
              : t("Seleziona o trascina il file", "Select or drag the file")}
        </Typography>

        {file && !uploading && (
          <div
            className="icon-remove-file"
            style={{ flexShrink: 0, cursor: "pointer" }}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                if (file.id) {
                  await service.deleteDocument(file.id);
                  if (typeof refreshDocuments === "function")
                    await refreshDocuments();
                }
                setFile(null);
                setError(null);
              } catch (err) {
                setError(
                  t(
                    "Errore nell'eliminazione del documento",
                    "Error deleting the document",
                  ),
                );
              }
            }}
          />
        )}
      </div>

      {error ? (
        <Typography style={{ fontSize: "12px", color: "#bc2957", marginTop: "4px" }}>
          {error}
        </Typography>
      ) : (
        <Typography style={{ fontSize: "12px", color: "#1b2838", fontWeight: 700, marginTop: "4px" }}>
          {t("Dimensione massima del file 4,5 MB", "Maximum file size 4.5 MB")}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
    </>
  );

  if (noGrid) return content;

  return (
    <Grid item xs={12} md={4} sx={{ paddingTop: padding }}>
      {content}
    </Grid>
  );
};

export default DocumentUpload;
